import { c as _c } from "react/compiler-runtime";
import { DndContext, DragOverlay, pointerWithin, useDndMonitor, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import type { ColumnPinningState } from '@tanstack/react-table';
import { useCallback, useRef, useState, type ReactElement, type ReactNode, type RefObject } from 'react';
import type { ChartDataRow } from '../../types';
import { COLUMN_ID_DATA_ATTR, SCROLL_ACTIVATION_MARGIN_PX, SCROLL_MAX_STEP } from './constants';
import TableHeadCellDragPreview from './head/TableHeadCellDragPreview';
import type { ChartCardTable } from './tanstack';
import type { HeaderLabelData } from './types';
import { parseColumnIdFromDragId, parseColumnIdFromDropId, reorderColumnsOnDrop } from './utils/dnd';
import { useDefaultDndSensors } from 'shared/dnd/sensors';
const getHeaderCellClientRect = (headRoot: HTMLElement | null, columnId: string): DOMRect | null => {
  if (headRoot == null) {
    return null;
  }
  const candidates = headRoot.querySelectorAll(`[${COLUMN_ID_DATA_ATTR}]`);
  for (const node of candidates) {
    if (node instanceof HTMLElement && node.getAttribute(COLUMN_ID_DATA_ATTR) === columnId) {
      return node.getBoundingClientRect();
    }
  }
  return null;
};

/**
 * Horizontal auto-scroll while dragging: when columns are pinned, zones sit on the seams
 * (after last left-pinned header, before first right-pinned header). Otherwise zones use the
 * scroll container’s left/right edges.
 */
const applyHorizontalPinAwareAutoScroll = (container: HTMLElement, clientX: number, headRoot: HTMLElement | null, leftPinColumnIds: readonly string[], rightPinColumnIds: readonly string[]): void => {
  const containerRect = container.getBoundingClientRect();
  if (clientX < containerRect.left || clientX > containerRect.right) {
    return;
  }
  const lastLeftId = leftPinColumnIds.at(-1);
  const leftReferenceX = (lastLeftId == null ? undefined : getHeaderCellClientRect(headRoot, lastLeftId)?.right) ?? containerRect.left;
  const firstRightId = rightPinColumnIds[0];
  const rightReferenceX = (firstRightId == null ? undefined : getHeaderCellClientRect(headRoot, firstRightId)?.left) ?? containerRect.right;
  const distFromLeftRef = clientX - leftReferenceX;
  const distFromRightRef = rightReferenceX - clientX;
  if (Math.abs(distFromLeftRef) < SCROLL_ACTIVATION_MARGIN_PX) {
    const intensity = 1 - Math.abs(distFromLeftRef) / SCROLL_ACTIVATION_MARGIN_PX;
    container.scrollLeft -= SCROLL_MAX_STEP * intensity;
  } else if (Math.abs(distFromRightRef) < SCROLL_ACTIVATION_MARGIN_PX) {
    const intensity = 1 - Math.abs(distFromRightRef) / SCROLL_ACTIVATION_MARGIN_PX;
    container.scrollLeft += SCROLL_MAX_STEP * intensity;
  }
};
const ColumnDnDScrollMonitor = ({
  tableContainerRef,
  tableHeadRootRef,
  columnPinning
}: {
  tableContainerRef: RefObject<HTMLDivElement | null>;
  tableHeadRootRef: RefObject<HTMLElement | null>;
  columnPinning: ColumnPinningState;
}): null => {
  const pointerRef = useRef({
    x: 0,
    y: 0
  });
  const rafRef = useRef<number | undefined>(undefined);
  const moveListenerRef = useRef<((event: PointerEvent) => void) | null>(null);
  const stopScrolling = useCallback((): void => {
    if (moveListenerRef.current != null) {
      globalThis.removeEventListener('pointermove', moveListenerRef.current);
      moveListenerRef.current = null;
    }
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = undefined;
    }
  }, []);
  useDndMonitor({
    onDragStart(event: DragStartEvent) {
      const activator = event.activatorEvent;
      if ('clientX' in activator) {
        const pointerEvent = activator as PointerEvent;
        pointerRef.current = {
          x: pointerEvent.clientX,
          y: pointerEvent.clientY
        };
      }
      const onMove = (pointerEvent_0: PointerEvent): void => {
        pointerRef.current = {
          x: pointerEvent_0.clientX,
          y: pointerEvent_0.clientY
        };
      };
      moveListenerRef.current = onMove;
      globalThis.addEventListener('pointermove', onMove);
      const tick = (): void => {
        const el = tableContainerRef.current;
        if (el != null) {
          applyHorizontalPinAwareAutoScroll(el, pointerRef.current.x, tableHeadRootRef.current, columnPinning.left ?? [], columnPinning.right ?? []);
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    },
    onDragEnd: stopScrolling,
    onDragCancel: stopScrolling
  });
  return null;
};
interface TableColumnDnDProviderProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  tableRef: RefObject<ChartCardTable<DataKey, DataRow> | null>;
  tableContainerRef: RefObject<HTMLDivElement | null>;
  tableHeadRootRef: RefObject<HTMLElement | null>;
  allowColumnPinning: boolean;
  columnPinning: ColumnPinningState;
  children: ReactNode;
}
const TableColumnDnDProvider = t0 => {
  const $ = _c(19);
  const {
    tableRef,
    tableContainerRef,
    tableHeadRootRef,
    allowColumnPinning,
    columnPinning,
    children
  } = t0;
  const [activeData, setActiveData] = useState(null);
  const sensors = useDefaultDndSensors();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = event => {
      setActiveData(event.active.data.current as HeaderLabelData);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleDragStart = t1;
  let t2;
  if ($[1] !== allowColumnPinning || $[2] !== tableRef) {
    t2 = event_0 => {
      setActiveData(null);
      const {
        active,
        over
      } = event_0;
      const table = tableRef.current;
      if (table == null || over == null) {
        return;
      }
      const activeColumnId = parseColumnIdFromDragId(active.id);
      const overColumnId = parseColumnIdFromDropId(over.id);
      if (activeColumnId == null || overColumnId == null) {
        return;
      }
      const stateOrder = table.getState().columnOrder;
      const allIds = table.getAllLeafColumns().map(_temp);
      const columnOrder = stateOrder != null && stateOrder.length > 0 ? [...stateOrder] : [...allIds];
      const pinning = table.getState().columnPinning ?? {
        left: [],
        right: []
      };
      const {
        columnOrder: nextOrder,
        columnPinning: nextPinning
      } = reorderColumnsOnDrop({
        columnOrder,
        activeColumnId,
        overColumnId,
        pinning,
        allowColumnPinning
      });
      table.setColumnOrder(nextOrder);
      if (allowColumnPinning) {
        table.setColumnPinning(nextPinning);
      }
    };
    $[1] = allowColumnPinning;
    $[2] = tableRef;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleDragEnd = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setActiveData(null);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleDragCancel = t3;
  let t4;
  if ($[5] !== columnPinning || $[6] !== tableContainerRef || $[7] !== tableHeadRootRef) {
    t4 = <ColumnDnDScrollMonitor tableContainerRef={tableContainerRef} tableHeadRootRef={tableHeadRootRef} columnPinning={columnPinning} />;
    $[5] = columnPinning;
    $[6] = tableContainerRef;
    $[7] = tableHeadRootRef;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== activeData) {
    t5 = activeData == null ? null : <TableHeadCellDragPreview data={activeData} width={200} textAlign="left" />;
    $[9] = activeData;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <DragOverlay dropAnimation={null}>{t5}</DragOverlay>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== children || $[14] !== handleDragEnd || $[15] !== sensors || $[16] !== t4 || $[17] !== t6) {
    t7 = <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>{t4}{children}{t6}</DndContext>;
    $[13] = children;
    $[14] = handleDragEnd;
    $[15] = sensors;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};
export default TableColumnDnDProvider;
function _temp(c) {
  return c.id;
}